<template>

  <el-alert
    v-if="shopCtx.listTip"
    class="mb-3!"
    :title="shopCtx.listTip"
    :type="shopCtx.multiSelfAbnormal || shopCtx.emptySelfShops ? 'warning' : 'info'"
    show-icon
    :closable="false"
  />
  <!-- 搜索工作栏 -->
  <ContentWrap>
    <el-form
      class="-mb-15px"
      :model="queryParams"
      ref="queryFormRef"
      :inline="true"
      label-width="100px"
    >
      <el-form-item
        v-if="shopCtx.selfScope ? shopCtx.multiSelfAbnormal : true"
        label="所属店铺"
        prop="shopId"
      >
        <el-select
          v-model="selectedShopId"
          class="!w-240px"
          clearable
          filterable
          :placeholder="shopCtx.selfScope ? '全部自营店汇总' : '全部'"
        >
          <el-option
            v-for="item in shopFilterOptions"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="模板名称" prop="name">
        <el-input
          v-model="queryParams.name"
          placeholder="请输入模板名称"
          clearable
          @keyup.enter="handleQuery"
          class="!w-240px"
        />
      </el-form-item>
      <el-form-item label="计费方式" prop="chargeMode">
        <el-select
          v-model="queryParams.chargeMode"
          placeholder="计费方式"
          clearable
          class="!w-240px"
        >
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.EXPRESS_CHARGE_MODE)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon icon="ep:search" class="mr-5px" /> 搜索</el-button>
        <el-button @click="resetQuery"><Icon icon="ep:refresh" class="mr-5px" /> 重置</el-button>
        <el-button
          type="primary"
          plain
          @click="openForm('create')"
          v-hasPermi="['trade:delivery:express-template:create']"
        >
          <Icon icon="ep:plus" class="mr-5px" />
          新增
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <!-- 列表 -->
  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column label="编号" min-width="60" prop="id" />
      <el-table-column label="所属店铺" min-width="100">
        <template #default="{ row }">{{ shopName(row.shopId) }}</template>
      </el-table-column>
      <el-table-column label="模板名称" min-width="100" prop="name" />
      <el-table-column label="计费方式" prop="chargeMode" min-width="100" align="center">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.EXPRESS_CHARGE_MODE" :value="scope.row.chargeMode" />
        </template>
      </el-table-column>
      <el-table-column label="排序" min-width="100" prop="sort" />
      <el-table-column
        label="创建时间"
        align="center"
        prop="createTime"
        width="180"
        :formatter="dateFormatter"
      />
      <el-table-column label="操作" align="center">
        <template #default="scope">
          <el-button
            link
            type="primary"
            @click="openForm('update', scope.row.id)"
            v-hasPermi="['trade:delivery:express-template:update']"
          >
            编辑
          </el-button>
          <el-button
            link
            type="danger"
            @click="handleDelete(scope.row.id)"
            v-hasPermi="['trade:delivery:express-template:delete']"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
  </ContentWrap>

  <!-- 表单弹窗：添加/修改 -->
  <ExpressTemplateForm ref="formRef" @success="getList" />
</template>
<script lang="ts" setup>
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import { dateFormatter } from '@/utils/formatTime'
import * as DeliveryExpressTemplateApi from '@/api/mall/trade/delivery/expressTemplate'
import ExpressTemplateForm from './ExpressTemplateForm.vue'
import { useMallSelfShopContext } from '@/hooks/web/useMallSelfShopContext'
import { getShopSimpleList } from '@/api/merchant/shop-manager'
import { useShopName } from '@/hooks/web/useShopName'

defineOptions({ name: 'DeliveryExpressTemplate' })

const message = useMessage() // 消息弹窗
const { t } = useI18n() // 国际化
const { shopName } = useShopName()
const {
  context: shopCtx,
  applyShopFilterToQuery,
  refresh: refreshShopCtx
} = useMallSelfShopContext()
const total = ref(0) // 列表的总页数
const loading = ref(true) // 列表的加载中
const list = ref<any[]>([]) // 列表的数据
const selectedShopId = ref<number | undefined>()
const shopFilterOptions = ref<{ id: number; name: string }[]>([])
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  name: '',
  chargeMode: undefined as number | undefined,
  shopId: undefined as number | undefined,
  shopIds: undefined as number[] | undefined
})
const queryFormRef = ref() // 搜索的表单

const refreshShopFilterOptions = async () => {
  await refreshShopCtx()
  if (shopCtx.value.selfScope) {
    shopFilterOptions.value = shopCtx.value.shops.map((s) => ({
      id: s.id,
      name: s.name || `店铺${s.id}`
    }))
    if (shopCtx.value.singleShopId) {
      selectedShopId.value = shopCtx.value.singleShopId
    } else if (
      selectedShopId.value &&
      !shopFilterOptions.value.some((s) => s.id === selectedShopId.value)
    ) {
      selectedShopId.value = undefined
    }
  } else {
    const shops = (await getShopSimpleList()) as { id: number; name: string }[]
    shopFilterOptions.value = (shops || []).map((s) => ({
      id: s.id,
      name: s.name || `店铺${s.id}`
    }))
    selectedShopId.value = undefined
  }
}

/** 查询列表 */
const getList = async () => {
  loading.value = true
  try {
    const params = { ...queryParams } as Record<string, any>
    if (shopCtx.value.selfScope) {
      applyShopFilterToQuery(params, selectedShopId.value)
    } else {
      delete params.shopIds
      params.shopId = selectedShopId.value
    }
    const data = await DeliveryExpressTemplateApi.getDeliveryExpressTemplatePage(params)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

/** 搜索按钮操作 */
const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

/** 重置按钮操作 */
const resetQuery = async () => {
  queryFormRef.value.resetFields()
  await refreshShopFilterOptions()
  handleQuery()
}

/** 添加/修改操作 */
const formRef = ref()
const openForm = (type: string, id?: number) => {
  formRef.value.open(type, id)
}

/** 删除按钮操作 */
const handleDelete = async (id: number) => {
  try {
    // 删除的二次确认
    await message.delConfirm()
    // 发起删除
    await DeliveryExpressTemplateApi.deleteDeliveryExpressTemplate(id)
    message.success(t('common.delSuccess'))
    // 刷新列表
    await getList()
  } catch {}
}

watch(
  () => shopCtx.value.selfScope,
  async () => {
    await refreshShopFilterOptions()
    handleQuery()
  }
)

/** 初始化 **/
onMounted(async () => {
  await refreshShopFilterOptions()
  getList()
})
</script>

